<script>
import { $ } from './util';

/* eslint no-nested-ternary: 0 */
const win = typeof window === 'undefined' ? {} : window;
const navigator = win.navigator || {};
const ua = navigator.userAgent || navigator.appVersion;
const vendor = /webkit/i.test(ua)
  ? 'webkit'
  : /firefox/i.test(ua)
  ? 'moz'
  : 'opera' in win
  ? 'o'
  : '';
const cssVendor = vendor ? `-${vendor.toLowerCase()}-` : '';

const PROP_TRANSITION = `${cssVendor}transition`;
const PROP_TRANSFORM = `${cssVendor}transform`;

const hasTouch = !!('ontouchstart' in win || navigator.maxTouchPoints);

const DIR_PREV = 'prev';
const DIR_NEXT = 'next';

// unable to detect the exact event
const EV_TRANSITION_END = ['transitionend', 'OTransitionEnd', 'webkitTransitionEnd'];

const EV_TOUCH_START = 'touchstart';
const EV_TOUCH_END = 'touchend';
const EV_TOUCH_MOVE = 'touchmove';

const EV_MOUSE_ENTER = 'mouseenter';
const EV_MOUSE_LEAVE = 'mouseleave';

const EV_MOUSE_DOWN = 'mousedown';
const EV_MOUSE_UP = 'mouseup';
const EV_MOUSE_MOVE = 'mousemove';

// Custom events
const EV_CHANGED_INDEX = 'changed-index';
const EV_RENDER_UPDATED = 'render-updated';

/*
  Events = {
  EV_CHANGED_INDEX,
  EV_RENDER_UPDATED,
  EV_NEXT,
  EV_PREV,
  EV_TO
  },
  */

const {
  on: bindEvent,
  off: unbindEvent,
  one: oneEvent,
  css: doCSS,
  width: getWidth,
  offset: getOffset,
} = $;

function roundUp(oVal) {
  return round(oVal, Math.ceil);
}

function roundDown(oVal) {
  return round(oVal, Math.floor);
}

function round(oVal, method) {
  const val = parseInt(oVal, 10);
  return method(val / 100) * 100;
}

/*
  // named exports are not supported in *.vue files. Shoot! (╯°Д°)╯︵ ┻━┻
  export {
  Events
  };
  */

export default {
  props: {
    // HTML content of the previous button.
    prevHtml: {
      type: String,
      default: '&lt;',
    },
    // HTML content of the enext button.
    nextHtml: {
      type: String,
      default: '&gt;',
    },
    // The time of the transition animation. In ms.
    speed: {
      type: Number,
      default: 300,
    },
    /*
      It can go to next/previous slide at the ends if it's set to true.
      It works only the items' length more than 1.
      */
    loop: {
      type: Boolean,
      default: false,
    },
    /*
      Rewind to the other end instead of endless loop,
      but you can only go to the other end by previous or next button,
      if it's set to true. It works only loop is set to true.
      */
    rewind: {
      type: Boolean,
      default: false,
    },
    // It can be drag by mouse if it's set to true.
    mouseDrag: {
      type: Boolean,
      default: false,
    },
    // It can be drag by touch if it's set to true.
    touchDrag: {
      type: Boolean,
      default: true,
    },
    // Autoplay interval. In ms. 0 for no autoplay.
    auto: {
      type: Number,
      default: 0,
    },
    // Pagination is available if it's set to true.
    dots: {
      type: Boolean,
      default: true,
    },
    // Style of v-carousel-dots
    dotsStyle: {
      type: [Object, String, Array],
      default: '',
    },
    /*
      The original data list used to render the CarouselItems.
      The component will rerender if this property changes.
      */
    watchItems: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      // It seems that key with symbols can't be accessced directly from vm.
      activeIndex: 0,
      animPaused: true,
      itemsLen: 0,
      slideCount: 0,
      hasLoop: false,
      autoTimer: null,
      // Nodes
      cloneItems: [],
      // Callbacks
      transEndCB: null,
    };
  },
  mounted() {
    const me = this;

    const { checkDrag } = me;

    // eslint-disable-next-line
      const updateRender = function () {
      me.updateRender();
      emitChangedIndex();
      emit(EV_RENDER_UPDATED);
    };

    function watch(k, v) {
      me.$watch(k, v, { deep: Array.isArray(me[k]) });
    }

    function emit(e, v) {
      me.$emit(e, v);
    }

    function emitChangedIndex() {
      const index = me.activeIndex;
      emit(EV_CHANGED_INDEX, {
        index,
        total: me.itemsLen,
        item: me.watchItems[index],
      });
    }

    watch('watchItems', updateRender);
    watch('loop', updateRender);
    watch('rewind', updateRender);
    watch('mouseDrag', checkDrag);
    watch('touchDrag', checkDrag);
    watch('auto', me.checkAuto);
    watch('activeIndex', emitChangedIndex);

    checkDrag();
    updateRender();

    bindEvent(me.$refs.itemsWrap, EV_TRANSITION_END, me.checkTrans);
  },
  /*
    Although "updated" can be used to detect content changes,
    it'll bring too many changes which are not I want.
    So use $watch instead.
    */
  unmount() {
    const me = this;
    me.unbindDrag();
    unbindEvent(me.$refs.itemsWrap, EV_TRANSITION_END, me.checkTrans);
  },
  methods: {
    updateRender() {
      const me = this;

      const {
        loop,
        watchItems,
        rewind,
        $refs: { itemsWrap },
      } = me;
      const itemsLen = watchItems.length;
      const hasLoop = loop && itemsLen > 1;
      const slideCount = hasLoop && !rewind ? itemsLen + 2 : itemsLen;

      let $cloneItems = me.cloneItems;
      let items;

      // clean up
      if ($cloneItems.length > 0) {
        $cloneItems.forEach((item) => item.parentNode.removeChild(item));
        $cloneItems = [];
        me.cloneItems = $cloneItems;
      }

      items = itemsWrap.querySelectorAll('.v-carousel-item');

      // create cloned nodes
      if (hasLoop && !rewind) {
        const firstNode = items[0];
        const lastNode = items[itemsLen - 1];
        const firstNodeCloned = firstNode.cloneNode(true);
        const lastNodeCloned = lastNode.cloneNode(true);
        itemsWrap.appendChild(firstNodeCloned);
        itemsWrap.insertBefore(lastNodeCloned, itemsWrap.firstChild);

        $cloneItems.push(firstNodeCloned);
        $cloneItems.push(lastNodeCloned);
        $cloneItems.forEach((item) => item.classList.add('cloned'));

        items = itemsWrap.querySelectorAll('.v-carousel-item');
      }

      me.hasLoop = hasLoop;
      me.itemsLen = itemsLen;
      me.slideCount = slideCount;

      doCSS(itemsWrap, 'width', `${100 * slideCount}%`);
      items.forEach((element) => doCSS(element, 'width', `${100 / slideCount}%`));

      me.rmAnim();
      me.reset();
      me.addAnim();
      me.checkAuto();
    },
    reset() {
      const me = this;
      me.off();
      me.to(0);
    },
    checkDrag() {
      const me = this;
      const {
        mouseDrag,
        touchDrag,
        $refs: { itemsWrap },
        startCB,
      } = me;

      const EV_MOVE = [];
      const EV_END = [];
      me.unbindDrag();

      if (mouseDrag) {
        // DragSnap support
        EV_MOVE.push(EV_MOUSE_MOVE);
        EV_END.push(EV_MOUSE_UP);
        bindEvent(itemsWrap, EV_MOUSE_DOWN, startCB);
      }

      if (touchDrag && hasTouch) {
        EV_MOVE.push(EV_TOUCH_MOVE);
        EV_END.push(EV_TOUCH_END);
        bindEvent(itemsWrap, EV_TOUCH_START, startCB);
      }

      me.EV_MOVE = EV_MOVE;
      me.EV_END = EV_END;
    },
    unbindDrag() {
      const me = this;
      unbindEvent(me.$refs.itemsWrap, [EV_TOUCH_START, EV_MOUSE_DOWN], me.startCB);
    },
    checkAuto() {
      const me = this;
      const {
        auto,
        off: turnOff,
        on: turnOn,
        $refs: { itemsWrap },
      } = me;

      unbindEvent(itemsWrap, EV_MOUSE_ENTER, turnOff);
      unbindEvent(itemsWrap, EV_MOUSE_LEAVE, turnOn);
      unbindEvent(itemsWrap, [EV_TOUCH_START, EV_MOUSE_DOWN], turnOff);
      unbindEvent(itemsWrap, [EV_TOUCH_END, EV_MOUSE_UP], turnOn);

      turnOff();

      if (auto > 0) {
        bindEvent(itemsWrap, EV_MOUSE_ENTER, turnOff);
        bindEvent(itemsWrap, EV_MOUSE_LEAVE, turnOn);
        bindEvent(itemsWrap, [EV_TOUCH_START, EV_MOUSE_DOWN], turnOff);
        bindEvent(itemsWrap, [EV_TOUCH_END, EV_MOUSE_UP], turnOn);
        turnOn();
      }
    },
    rmAnim() {
      const me = this;
      // reset reset animation
      doCSS(me.$refs.itemsWrap, PROP_TRANSITION, 'none');
      me.animPaused = true;
    },
    addAnim() {
      const me = this;
      const { itemsWrap } = me.$refs;
      // Force to paint
      getOffset(itemsWrap);

      doCSS(itemsWrap, PROP_TRANSITION, `${PROP_TRANSFORM} ${me.speed / 1000}s ease`);
      me.animPaused = false;
    },
    on() {
      const me = this;
      const { hasLoop, itemsLen, auto } = me;
      me.off();
      if (itemsLen > 1 && auto > 0) {
        me.autoTimer = setInterval(() => {
          if (!hasLoop) {
            if (me.activeIndex === itemsLen - 1) {
              me.to(0);
            } else {
              me.next();
            }
          } else {
            me.next();
          }
        }, auto);
      }
    },
    off() {
      const me = this;
      if (me.autoTimer) {
        clearInterval(me.autoTimer);
        me.autoTimer = null;
      }
    },
    next() {
      this.nextPrev(DIR_NEXT);
    },
    prev() {
      this.nextPrev(DIR_PREV);
    },
    nextPrev(dir) {
      const me = this;
      const { hasLoop, rewind, itemsLen, activeIndex } = me;
      const lastItemIndex = itemsLen - 1;
      let index;

      if (dir === DIR_PREV) {
        if (activeIndex === 0) {
          if (hasLoop) {
            if (rewind) {
              index = lastItemIndex;
            } else {
              index = -1;
            }
          } else {
            return;
          }
        } else {
          index = activeIndex - 1;
        }
      } else if (activeIndex === itemsLen - 1) {
        // next
        if (hasLoop) {
          if (rewind) {
            index = 0;
          } else {
            index = itemsLen;
          }
        } else {
          return;
        }
      } else {
        index = activeIndex + 1;
      }
      me.to(index);
    },
    to(index) {
      const me = this;
      const { hasLoop, itemsLen, slideCount, rewind } = me;
      const lastItemIndex = itemsLen - 1;

      const removeAnimation = me.rmAnim;
      const addAnimation = me.addAnim;
      const go = me.to;

      let realIndex;
      let onSlideEnd;

      if (hasLoop) {
        if (index === -1) {
          // eslint-disable-next-line
          onSlideEnd = function () {
            removeAnimation();
            go(lastItemIndex);
            addAnimation();
          };
        } else if (index === itemsLen) {
          // eslint-disable-next-line
          onSlideEnd = function () {
            removeAnimation();
            go(0);
            addAnimation();
          };
        }

        realIndex = !rewind ? index + 1 : index;
      } else {
        realIndex = index;
      }

      if (!onSlideEnd) {
        if (me.animPaused) {
          me.activeIndex = index;
        } else {
          // eslint-disable-next-line
          onSlideEnd = function () {
            me.activeIndex = index;
          };
        }
      }

      me.transEndCB = onSlideEnd;
      me.transTo((realIndex * -100) / slideCount);
    },
    checkTrans() {
      const me = this;
      const { transEndCB } = me;
      // only fire once
      if (transEndCB) {
        transEndCB();
        me.transEndCB = null;
      }
    },
    transTo(moveTo) {
      doCSS(this.$refs.itemsWrap, PROP_TRANSFORM, `translate3d(${moveTo}%,0,0)`);
    },
    startCB(e) {
      /* eslint no-use-before-define: 0 */
      const me = this;
      const {
        EV_MOVE,
        EV_END,

        transTo,
      } = me;

      const getEventData = me.getEv;
      const removeAnimation = me.rmAnim;
      const addAnimation = me.addAnim;

      const data = getEventData(e);

      const { itemsWrap, outer } = me.$refs;
      const elWidth = getWidth(outer);
      const currentPos = getCurrentPos(itemsWrap);

      let start = {
        time: Date.now(),
        coords: [data.pageX, data.pageY],
        interacting: false,
      };
      let stop;
      let deltaX;
      let deltaY;

      removeAnimation();

      function getCurrentPos($wrap) {
        const match = $wrap
          .getAttribute('style')
          ?.match(/transform:.*translate3d.*\((.*[0-9].*)%/i);
        return match ? Number(match[1]) : 0;
      }

      function moveHandler(ev) {
        if (!start) return;

        const d = getEventData(ev);
        stop = {
          time: Date.now(),
          coords: [d.pageX, d.pageY],
        };

        deltaX = Math.abs(start.coords[0] - stop.coords[0]);
        deltaY = Math.abs(start.coords[1] - stop.coords[1]);

        // move threashold
        if (deltaX < deltaY || deltaY > 10 || deltaX < 3) {
          return;
        }

        // prevent scrolling
        if (deltaX >= 3) {
          start.interacting = true;
          const percent =
            currentPos + (((stop.coords[0] - start.coords[0]) / elWidth) * 100) / me.slideCount;
          transTo(percent);
          ev.preventDefault();
        }
      }

      function snapback($wrap, left) {
        const { slideCount } = me;
        const pos = getCurrentPos($wrap) * slideCount;
        const percent = (left ? roundUp(pos) : roundDown(pos)) / slideCount;

        transTo(percent);
      }

      function dragsnap(dir) {
        me.nextPrev(dir);
      }

      bindEvent(itemsWrap, EV_MOVE, moveHandler);
      oneEvent(itemsWrap, EV_END, (ev) => {
        const { activeIndex } = me;
        unbindEvent(itemsWrap, EV_MOVE, moveHandler);

        addAnimation();

        if (start && stop) {
          const dx = Math.abs(start.coords[0] - stop.coords[0]);
          const dy = Math.abs(start.coords[1] - stop.coords[1]);
          const left = start.coords[0] > stop.coords[0];
          const jumppoint = elWidth / 4;

          // move threashold
          if (dx > 20 && dx > dy) {
            ev.preventDefault();
          } else {
            if (start.interacting) {
              snapback(itemsWrap, left);
            }
            return;
          }

          if (
            dx > jumppoint &&
            ((me.hasLoop && !me.rewind) ||
              (!left && activeIndex > 0) ||
              (left && activeIndex < me.itemsLen - 1))
          ) {
            dragsnap(left ? DIR_NEXT : DIR_PREV);
          } else {
            snapback(itemsWrap, left);
          }
        }
        start = null;
        stop = null;
      });
    },
    getEv(e) {
      let obj;
      if (e.targetTouches) {
        // eslint-disable-next-line
        obj = e.targetTouches[0];
      } else {
        obj = e;
      }

      return obj;
    },
  },
};
</script>

<template>
  <!--
    Don't use v-bind to bind events which are not available,
    Otherwise Vue will go on rendering with many bugs sliently as in many other places.
  -->
  <div class="v-carousel" ref="outer">
    <slot name="before" />
    <div class="v-carousel-items" ref="itemsWrap">
      <slot />
    </div>
    <div class="v-carousel-dots" :style="dotsStyle" v-if="dots">
      <div
        :class="{ 'v-carousel-dot': true, active: activeIndex == index }"
        @click="to(index)"
        v-for="(item, index) in watchItems"
        :key="index + ''"
      />
    </div>
    <div
      class="v-carousel-nav prev"
      @click="prev"
      v-show="hasLoop || (itemsLen > 1 && activeIndex > 0)"
      v-html="prevHtml"
    />
    <div
      class="v-carousel-nav next"
      @click="next"
      v-show="hasLoop || (itemsLen > 1 && activeIndex < itemsLen - 1)"
      v-html="nextHtml"
    />
    <slot name="after" />
  </div>
</template>

<style lang="scss">
.v-carousel {
  @include respond-min($screen-md-min) {
    display: none;
  }
  &-items {
    overflow: hidden;
    max-height: 256px;
    display: flex;
  }
  &-dots {
    position: absolute;
    bottom: 10px;
    left: 0;
    width: 100%;
    text-align: center;
    div:only-child {
      display: none;
    }
  }
  &-dot {
    &.active {
      background: $eastern-blue;
      cursor: default;
    }
    cursor: pointer;
    display: inline-block;
    width: 7px;
    height: 7px;
    margin: 5px 7px;
    border-radius: 30px;
    background: #d6d6d6;
    transition: opacity 200ms ease;
  }
  &-nav {
    &.prev {
      left: 0;
    }
    &.next {
      right: 0;
    }
    display: none;
    cursor: pointer;
    position: absolute;
    line-height: 30px;
    color: #fff;
    padding: 0 5px;
    background: rgba(0, 0, 0, 0.5);
    top: 50%;
    margin-top: -15px;
  }
  width: 100%;
  overflow: hidden;
  position: relative;
}
</style>
